import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { shallowEqual, useDispatch } from 'react-redux';

import { Page, PageContent, UpsellBanner } from '@zillow/constellation';
import { Heading, Text, TextButton } from '@zillow/constellation-10';

import { useSelector } from '../../selectors/useSelector';

import { updateIsWizardView } from '../../slices/app';

import FeatureNotificationsCSEvents from '../../model/ClickstreamEvent/FeatureNotificationsCSEvents';

import type { ReactNode } from 'react';

type Notification = {
  url: string;
  icon: string;
  name: string;
  ctaText: string;
  description: string;
  title: string;
};

const BannerPreviewPage = () => {
  const dispatch = useDispatch();

  const { search } = useLocation();

  const { isWizardView, user } = useSelector(
    (state) => ({
      isWizardView: state?.app?.isWizardView || false,
      user: state?.user,
    }),
    shallowEqual,
  );

  const [iconComponent, setIconComponent] = useState<ReactNode>(null);

  const { notification } = useMemo(() => {
    const searchParams = new URLSearchParams(search);

    const notificationToBe: Record<string, string> = {};

    for (const [key, value] of searchParams.entries()) {
      notificationToBe[key] = value;
    }

    return { notification: notificationToBe as Notification };
  }, [search]);

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      if (!isWizardView) {
        dispatch(updateIsWizardView(true));
      }

      initialRender.current = false;
    }

    return () => {
      dispatch(updateIsWizardView(false));
    };
  }, []);

  useEffect(() => {
    if (notification?.icon) {
      const { [notification.icon]: NewIcon } = require('@zillow/constellation');
      if (NewIcon) {
        setIconComponent(<NewIcon />);
      }
    } else if (iconComponent) {
      setIconComponent(null);
    }
  }, [notification]);

  const handleCTAClick = () => {
    FeatureNotificationsCSEvents.LearnMore({ user, notificationTitle: notification?.title });
    window.open(notification.url, '_blank', 'noopener, noreferrer');
  };

  return (
    <Page data-testid="BannerPreviewPage">
      <PageContent data-testid="UpsellBanner-container">
        <UpsellBanner
          actionButton={
            notification.ctaText &&
            notification.url && (
              <TextButton
                onClick={() => handleCTAClick()}
                data-eventid={FeatureNotificationsCSEvents.LearnMore.data_event_id}
              >
                {notification.ctaText}
              </TextButton>
            )
          }
          heading={notification.title && <Heading level={6}>{notification.title}</Heading>}
          body={notification.description && <Text>{notification.description}</Text>}
          media={iconComponent}
          showBody={true}
        />
      </PageContent>
    </Page>
  );
};

export default BannerPreviewPage;
